import React, { useCallback, useMemo } from 'react';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetAiInterviewerRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { z } from 'zod';
import { DataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeRecipeDataWizardTrackerItem } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useDataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { DataSetCompositeCompleteIndicator } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeCompleteIndicator';
import { TransformEditorUI } from 'apps/sigma2-ui/src/data-wizard/components/transform-page/TransformEditorUI';
import { DataSetCompositeRecipeAIInterviewerRubricConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeAIInterviewerRubricConfigComponent';

const AiInterviewerConfigStep = z.enum([
  'script',
  'rubric',
  'final-review',
  'complete',
]);

type AiInterviewerConfigStep = z.infer<typeof AiInterviewerConfigStep>;

export const DataSetCompositeRecipeAiInterviewerConfigComponent = ({
  organizationId,
  databaseId,
  config,
  setConfig,
}: {
  organizationId: string;
  databaseId: string;
  config: CompositeDataSetAiInterviewerRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
}) => {
  const stepItems: DataSetCompositeRecipeDataWizardTrackerItem<AiInterviewerConfigStep>[] =
    useMemo(() => {
      return [
        { value: 'script', display: 'Script' },
        { value: 'rubric', display: 'Rubric' },
        { value: 'final-review', display: 'Final Review' },
        { value: 'complete', display: 'Complete' },
      ];
    }, []);

  const onComplete = useCallback(() => {
    setConfig({
      ...config,
      script: config.script,
    });
  }, [config, setConfig]);

  const onNext = useCallback(
    (currentItem: AiInterviewerConfigStep) => {
      if (currentItem === 'complete') {
        onComplete();
      }

      return true;
    },
    [onComplete],
  );

  const setCode = useCallback(
    (code: string) => {
      setConfig({
        ...config,
        script: code,
      });
    },
    [config, setConfig],
  );

  const dataWizardPayload =
    useDataSetCompositeRecipeDataWizardTracker<AiInterviewerConfigStep>({
      items: stepItems,
      onNext,
    });

  const currentUiToShow = useMemo(() => {
    if (dataWizardPayload.selectedItem === null) {
      return null;
    }

    switch (dataWizardPayload.selectedItem) {
      case 'script':
        return (
          <TransformEditorUI
            code={config.script}
            setCode={setCode}
            isRunningExecution={false}
            isLoadingScript={false}
            runOnBatch={false}
            setRunOnBatch={() => {}}
            transformType={'python'}
            setTransformType={() => {}}
            platformTransformDetails={null}
            setPlatformTransformDetails={() => {}}
            isToBeCompared={false}
            setIsToBeCompared={() => {}}
            entitledDagsterTransforms={[]}
            selectedDagsterTransformId={null}
            setSelectedDagsterTransformId={() => {}}
          />
        );
      case 'rubric':
        return (
          <DataSetCompositeRecipeAIInterviewerRubricConfigComponent
            organizationId={organizationId}
            databaseId={databaseId}
            config={config}
            setConfig={setConfig}
          />
        );
      case 'final-review':
        return (
          <Spacing size={'lg'} direction={'vertical'}>
            <Box>
              <div className="text-lg font-semibold mb-4">Script Review</div>
              <div className="bg-gray-100 p-4 rounded-md">
                <pre className="whitespace-pre-wrap m-0">{config.script}</pre>
              </div>
            </Box>

            <Box>
              <div className="text-lg font-semibold mb-4">Configuration</div>
              <div className="space-y-2">
                <div>
                  <span className="font-medium">OpenAI Model:</span>{' '}
                  {config.openAiModel}
                </div>
                <div>
                  <span className="font-medium">
                    Hallucination Filter Enabled:
                  </span>{' '}
                  {config.enableHallucinationFilter ? 'Yes' : 'No'}
                </div>
                <div>
                  <span className="font-medium">
                    Combine Answers in Pipeline:
                  </span>{' '}
                  {config.combineAnswersInPipeline ? 'Yes' : 'No'}
                </div>
                <div>
                  <span className="font-medium">Bad Language Detector:</span>{' '}
                  {config.runBadLanguageDetector ? 'Yes' : 'No'}
                </div>
              </div>
            </Box>

            <Box>
              <div className="text-lg font-semibold mb-4">Rubric Review</div>
              <Table
                columnDefinitions={[
                  {
                    id: 'question',
                    header: () => <div>Filter</div>,
                    cell: ({ item }) => <div>{item.filter.type}</div>,
                  },
                  {
                    id: 'description',
                    header: () => <div>Construct ID</div>,
                    cell: ({ item }) => <div>{item.rubricConstructId}</div>,
                  },
                ]}
                items={config.rubricsByJobTitle}
              />
            </Box>
          </Spacing>
        );
      case 'complete':
        return <DataSetCompositeCompleteIndicator />;
      default:
        throw guardExhaustive(dataWizardPayload.selectedItem);
    }
  }, [
    dataWizardPayload.selectedItem,
    config,
    setCode,
    organizationId,
    databaseId,
    setConfig,
  ]);

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <DataSetCompositeRecipeDataWizardTracker payload={dataWizardPayload} />
      {currentUiToShow}
      {dataWizardPayload.paginationButtons}
    </Spacing>
  );
};
